/**
 * LandRegisterSheet — one land-register sheet (name/district → floors → parcels).
 *
 * Extracted from the legacy `PropertyInformationStep.controller` sheet sub-tree
 * (`controller.tsx:206-361`) to keep the orchestrator under 300 lines. All data
 * wiring is rewritten onto the module's `sheetOps` (plain-array CRUD by index);
 * ZERO `PurchaseFlow/*` imports. Dead-in-v2 gates removed (spec §4a): the
 * mandatory-name/landlord `required`+error framing and the `€{feePrice}`
 * disclaimer are gone (searchProduct never exists in v2).
 */

import { GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { Collapse } from '@mui/material'
import React from 'react'

import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { TransitionGroup } from 'react-transition-group'
import { useTranslation } from 'react-i18next'
import { trimToNull } from 'utils/helpers/stringUtils'

import type { LandRegistrationSheetDTO } from 'models/purchaseFlow/AuthoritiesDocumentDTO'

import { isLandRegisterNameValid } from '../propertyInfo.dto'
import type { PropertyInfoSheetOps } from '../usePropertyInfoConfig'
import { MUIIconButton } from 'components/common/MUIIconButton'

export interface LandRegisterSheetProps {
  sheet: LandRegistrationSheetDTO
  sheetIndex: number
  /** The first sheet cannot be removed (parity: legacy hid remove on index 0). */
  isFirst: boolean
  /** Map mode (false) disables district/hallway/parcel; "Edit manually" flips it. */
  isEditManually: boolean
  /** Blur-validation flag owned by the orchestrator. */
  showError: boolean
  sheetOps: PropertyInfoSheetOps
  onBlur: () => void
  onEditManually: () => void
}

/**
 * @component LandRegisterSheet
 */
export const LandRegisterSheet: React.FC<LandRegisterSheetProps> = ({
  sheet,
  sheetIndex,
  isFirst,
  isEditManually,
  showError,
  sheetOps,
  onBlur,
  onEditManually,
}) => {
  const { t } = useTranslation(['purchase_flow', 'purchase_flow_revamp'])

  const nameHasError = !!trimToNull(sheet.name) && !isLandRegisterNameValid(sheet.name) && showError

  return (
    <SectionedBorderBox
      title={
        <Stack direction="row" justifyContent="space-between" alignItems="center">

          <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
            {t('property_information_step.land_register_sheet.title')}
          </Typography>

          {!isFirst && isEditManually &&
            <MUIIconButton variant="plain" size="lg" icon={<CloseRoundedIcon />} ariaLabel="delete-land-register-sheet" onClick={() => sheetOps.removeSheet(sheetIndex)} />
          }

          {!isEditManually &&
            <MUIButton type="secondaryBorder" size="sm" onClick={onEditManually}>
              <Typography variant="text-xs">{t('property_information_step.edit_manually')}</Typography>
            </MUIButton>
          }

        </Stack>
      }
    >

      <Stack spacing={2} marginBottom={2} maxWidth="28%" width="100%">

        <MUIInputField
          isError={nameHasError}
          errorText={nameHasError ? t('purchase_flow_revamp:property_info.land_register_name_invalid') : undefined}
          label={t('property_information_step.land_register_sheet.input_land_register_sheet')}
          value={sheet.name}
          onBlur={onBlur}
          onChange={(e) => sheetOps.updateSheet(sheetIndex, { name: e.target.value })}
        />

        <MUIInputField
          required
          disabled={!isEditManually}
          isError={!sheet.district && showError}
          label={t('property_information_step.land_register_sheet.input_district')}
          value={sheet.district}
          onBlur={onBlur}
          onChange={(e) => sheetOps.updateSheet(sheetIndex, { district: e.target.value })}
        />

      </Stack>

      <Stack gap={2}>

        <TransitionGroup component={null}>
          {sheet.floors.map((floor, floorIndex) => (
            <Collapse key={`floor-${floorIndex}`}>
              <SectionedBorderBox
                title={
                  <Stack direction="row" justifyContent="space-between" alignItems="center">

                    <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                      {`${t('floor_title')} #${floorIndex + 1}`}
                    </Typography>

                    {sheet.floors.length > 1 && isEditManually &&
                      <MUIIconButton variant="plain" size="lg" icon={<CloseRoundedIcon />} ariaLabel="delete-floor" onClick={() => sheetOps.removeFloor(sheetIndex, floorIndex)} />
                    }

                  </Stack>
                }
              >

                <Stack maxWidth="28%" marginBottom={2}>
                  <MUIInputField
                    disabled={!isEditManually}
                    label={t('property_information_step.land_register_sheet.input_floor_hallway')}
                    value={floor.hallway}
                    onBlur={onBlur}
                    onChange={(e) => sheetOps.updateFloor(sheetIndex, floorIndex, { hallway: e.target.value })}
                  />
                </Stack>

                {floor.parcels.map((parcel, parcelIndex) => (
                  <Stack key={parcelIndex} gap="1rem" direction="row" alignItems="flex-end" flexWrap="wrap" width="100%" marginBottom={2}>

                    <Stack flex={1} maxWidth="28%">
                      <MUIInputField
                        required
                        disabled={!isEditManually}
                        isError={!parcel.number && showError}
                        label={t('property_information_step.land_register_sheet.input_floor_parcel')}
                        value={parcel.number}
                        onBlur={onBlur}
                        onChange={(e) => sheetOps.updateParcel(sheetIndex, floorIndex, parcelIndex, { number: e.target.value })}
                      />
                    </Stack>

                    {floor.parcels.length > 1 && isEditManually &&
                      <MUIButton
                        type="secondaryBorder"
                        onClick={() => sheetOps.removeParcel(sheetIndex, floorIndex, parcelIndex)}
                        startIcon={<RemoveCircleOutlineRoundedIcon fontSize="large" sx={{ color: GRAY_800 }} />}
                      >
                        {t('property_information_step.land_register_sheet.remove_parcel')}
                      </MUIButton>
                    }

                    {parcelIndex === floor.parcels.length - 1 && isEditManually &&
                      <MUIButton
                        type="secondaryBorder"
                        onClick={() => sheetOps.addParcel(sheetIndex, floorIndex)}
                        startIcon={<AddCircleOutlineRoundedIcon fontSize="large" sx={{ color: GRAY_800 }} />}
                      >
                        {t('property_information_step.land_register_sheet.add_parcel')}
                      </MUIButton>
                    }

                  </Stack>
                ))}

              </SectionedBorderBox>
            </Collapse>
          ))}
        </TransitionGroup>

        {isEditManually &&
          <Stack gap={2} direction="row" alignItems="flex-end" justifyContent="center">
            <MUIButton
              type="secondaryBorder"
              onClick={() => sheetOps.addFloor(sheetIndex)}
              startIcon={<AddCircleOutlineRoundedIcon fontSize="large" sx={{ color: GRAY_800 }} />}
            >
              {t('property_information_step.land_register_sheet.add_floor')}
            </MUIButton>
          </Stack>
        }

      </Stack>

    </SectionedBorderBox>
  )
}
